import React, { useEffect, useState, useRef } from "react";
import Navbar from "../Navbar";
import Loader from "../Loader";
import axios from "axios";
import { Config } from "../../Config";
import "../../assets/css/bazaar.css";

const PrivacyPolicy = () => {
  const [privacy, setPrivacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, []);

  useEffect(() => {
    if (fetched.current) {
      setLoading(false);
      return;
    }

    fetched.current = true;

    axios
      .get(`${Config.apiUrl}/privacy-policy`, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const data = response.data.privacy_policy;
        setPrivacy(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was a problem fetching the privacy policy data:", error);
        setLoading(false);
        setError(true);
      });
  }, []);

  // Skeleton component
  const SkeletonBanner = () => (
    <section
      className="py-5"
      style={{
        background: "#f8f9fa",
        position: "relative",
        color: "#6c757d",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          zIndex: 1,
        }}
      ></div>
      <div className="container-box py-md-5" style={{ position: "relative", zIndex: 2 }}>
        <div className="text-center">
          <div className="skeleton-title mb-3"></div>
          <div className="skeleton-subtitle"></div>
        </div>
      </div>
    </section>
  );

  const SkeletonDescription = () => (
    <section className="py-5">
      <div className="container-box py-md-2">
        <div className="text-center">
          <div className="skeleton-h1 mb-3"></div>
          <div className="skeleton-hr mb-3"></div>
        </div>
        <div style={{ minHeight: "40vh" }}>
          <div className="skeleton-text"></div>
          <div className="skeleton-text mt-3"></div>
          <div className="skeleton-text mt-3"></div>
        </div>
      </div>
    </section>
  );

  // Skeleton styles
  const skeletonStyles = `
    .skeleton-title { width: 60%; height: 40px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; margin: 0 auto; border-radius: 4px; }
    .skeleton-subtitle { width: 80%; height: 20px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; margin: 0 auto; border-radius: 4px; }
    .skeleton-h1 { width: 40%; height: 50px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; margin: 0 auto; border-radius: 4px; }
    .skeleton-hr { width: 100px; height: 3px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; margin: 10px auto; border-radius: 2px; }
    .skeleton-text { height: 20px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; border-radius: 4px; }
    .skeleton-text:nth-child(2) { width: 90%; margin: 0 auto; }
    .skeleton-text:nth-child(3) { width: 70%; margin: 0 auto; }
    @keyframes loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  `;

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = skeletonStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (loading || error || !privacy) {
    return (
      <>
        <style>{skeletonStyles}</style>
        <Navbar />
        <div className="page-content pb-5">
          <SkeletonBanner />
          <SkeletonDescription />
        </div>
      </>
    );
  }

  let bgImagePath = "";
  if (privacy?.privacy_bg_image) {
    let rawPath = `${Config.apiUrl}${privacy.privacy_bg_image}`;
    rawPath = rawPath.replace(/\/api\/?/, "/");
    bgImagePath = encodeURI(rawPath);
  }

  return (
    <>
      <Navbar />
      <div className="page-content pb-5">
        {/* Banner Section with BG Image */}
        <section
          className="py-5"
          style={{
            backgroundImage: `url(${bgImagePath})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
            color: "white",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1,
            }}
          ></div>
          <div className="container-box py-md-5" style={{ position: "relative", zIndex: 2 }}>
            <div className="text-center text-white">
              <h2 className="fw-bold text-capitalize mb-3">
                {privacy.privacy_title || "Privacy Policy"}
              </h2>
              <p className="lead text-center text-white">
                {privacy.privacy_subtitle || ""}
              </p>
            </div>
          </div>
        </section>

        {/* Description Section */}
        <section className="py-5">
          <div className="container-box py-md-2">
            <h1 className="text-center fw-bold">Privacy Policy</h1>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "10px 0",
              }}
            >
              <hr
                style={{
                  color: "red",
                  width: "100px",
                  borderTop: "3px solid red",
                }}
              />
            </div>
            <div style={{ minHeight: "40vh" }}>
              <div
                dangerouslySetInnerHTML={{ __html: privacy.privacy_description }}
              />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;